// src/store/knowledge-base/exceptions/knowledgeBaseExceptionsSlice.ts
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import {
    KBException,
    ExceptionsListQuery,
    KnowledgeBaseExceptionsService,
    ExceptionStatus,
    KnowledgeScope,
} from './exceptionsApi';

export interface KnowledgeBaseExceptionsState {
    list: KBException[];
    listIsLoading: boolean;
    detail: KBException | null;
    detailIsLoading: boolean;
    error: string | null;
}

const initialState: KnowledgeBaseExceptionsState = {
    list: [],
    listIsLoading: false,
    detail: null,
    detailIsLoading: false,
    error: null,
};

// List
export const tryToFetchKnowledgeBaseExceptions = createAsyncThunk<
    KBException[],
    ExceptionsListQuery | undefined
>(
    'knowledgeBaseExceptions/tryToFetchKnowledgeBaseExceptions',
    async (query, { rejectWithValue }) => {
        try {
            const res =
                await KnowledgeBaseExceptionsService.tryToFetchKnowledgeBaseExceptions(
                    query,
                );
            return res.data || [];
        } catch (error) {
            const err = error as AxiosError<any>;
            return rejectWithValue(
                (err.response?.data as any)?.message ||
                    'Failed to load exceptions',
            );
        }
    },
);

// Detail
export const tryToFetchKnowledgeBaseExceptionById = createAsyncThunk<
    KBException,
    string
>(
    'knowledgeBaseExceptions/tryToFetchKnowledgeBaseExceptionById',
    async (id, { rejectWithValue }) => {
        try {
            const res =
                await KnowledgeBaseExceptionsService.tryToFetchKnowledgeBaseExceptionById(
                    id,
                );
            return res.data;
        } catch (error) {
            const err = error as AxiosError<any>;
            return rejectWithValue(
                (err.response?.data as any)?.message ||
                    'Failed to load exception',
            );
        }
    },
);

// Update status
export const tryToUpdateKnowledgeBaseExceptionStatus = createAsyncThunk<
    KBException,
    { id: string; status: ExceptionStatus }
>(
    'knowledgeBaseExceptions/tryToUpdateKnowledgeBaseExceptionStatus',
    async (data, { rejectWithValue }) => {
        try {
            const res =
                await KnowledgeBaseExceptionsService.tryToUpdateKnowledgeBaseExceptionStatus(
                    data,
                );
            return res.data;
        } catch (error) {
            const err = error as AxiosError<any>;
            return rejectWithValue(
                (err.response?.data as any)?.message ||
                    'Failed to update status',
            );
        }
    },
);

// Resolve
export const tryToResolveKnowledgeBaseException = createAsyncThunk<
    KBException,
    {
        id: string;
        explanation: string;
        scope: KnowledgeScope;
        additionalNotes?: string;
    }
>(
    'knowledgeBaseExceptions/tryToResolveKnowledgeBaseException',
    async (data, { rejectWithValue }) => {
        try {
            const res =
                await KnowledgeBaseExceptionsService.tryToResolveKnowledgeBaseException(
                    data,
                );
            return res.data;
        } catch (error) {
            const err = error as AxiosError<any>;
            return rejectWithValue(
                (err.response?.data as any)?.message ||
                    'Failed to resolve exception',
            );
        }
    },
);

export const tryToResolveKnowledgeBaseExceptionConfirm = createAsyncThunk<
    KBException,
    { id: string; scope: KnowledgeScope; confirmationType: string }
>(
    'knowledgeBaseExceptions/tryToResolveKnowledgeBaseExceptionConfirm',
    async (data, { rejectWithValue }) => {
        try {
            const res =
                await KnowledgeBaseExceptionsService.tryToResolveKnowledgeBaseExceptionConfirm(
                    data,
                );
            return res.data;
        } catch (error) {
            const err = error as AxiosError<any>;
            return rejectWithValue(
                (err.response?.data as any)?.message ||
                    'Failed to confirm-resolve exception',
            );
        }
    },
);

export const tryToConfirmKnowledgeBaseException = createAsyncThunk<
    KBException,
    { id: string; confirmationType?: string }
>(
    'knowledgeBaseExceptions/tryToConfirmKnowledgeBaseException',
    async (data, { rejectWithValue }) => {
        try {
            const res =
                await KnowledgeBaseExceptionsService.tryToConfirmKnowledgeBaseException(
                    data,
                );
            return res.data;
        } catch (error) {
            const err = error as AxiosError<any>;
            return rejectWithValue(
                (err.response?.data as any)?.message ||
                    'Failed to confirm exception',
            );
        }
    },
);

export const tryToConfirmResolveKnowledgeBaseException = createAsyncThunk<
    KBException,
    { id: string; confirmationType?: string }
>(
    'knowledgeBaseExceptions/tryToConfirmResolveKnowledgeBaseException',
    async (data, { rejectWithValue }) => {
        try {
            const res =
                await KnowledgeBaseExceptionsService.tryToConfirmResolveKnowledgeBaseException(
                    data,
                );
            return res.data;
        } catch (error) {
            const err = error as AxiosError<any>;
            return rejectWithValue(
                (err.response?.data as any)?.message ||
                    'Failed to confirm resolve exception',
            );
        }
    },
);

export const knowledgeBaseExceptionsSlice = createSlice({
    name: 'knowledgeBaseExceptions',
    initialState,
    reducers: {
        // optional: allow clearing errors from UI
        clearKnowledgeBaseExceptionsError(state) {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // List
            .addCase(tryToFetchKnowledgeBaseExceptions.pending, (state) => {
                state.listIsLoading = true;
                state.error = null;
            })
            .addCase(
                tryToFetchKnowledgeBaseExceptions.rejected,
                (state, action) => {
                    state.listIsLoading = false;
                    state.error =
                        (action.payload as string) ||
                        'Failed to load exceptions';
                },
            )
            .addCase(
                tryToFetchKnowledgeBaseExceptions.fulfilled,
                (state, action: PayloadAction<KBException[]>) => {
                    state.listIsLoading = false;
                    state.list = action.payload;
                },
            )

            // Detail
            .addCase(tryToFetchKnowledgeBaseExceptionById.pending, (state) => {
                state.detailIsLoading = true;
                state.error = null;
            })
            .addCase(
                tryToFetchKnowledgeBaseExceptionById.rejected,
                (state, action) => {
                    state.detailIsLoading = false;
                    state.error =
                        (action.payload as string) ||
                        'Failed to load exception';
                },
            )
            .addCase(
                tryToFetchKnowledgeBaseExceptionById.fulfilled,
                (state, action: PayloadAction<KBException>) => {
                    state.detailIsLoading = false;
                    state.detail = action.payload;
                    // Optionally keep list in sync when we load detail
                    const idx = state.list.findIndex(
                        (x) => x._id === action.payload._id,
                    );
                    if (idx >= 0) {
                        state.list[idx] = action.payload;
                    }
                },
            )

            // Update status
            .addCase(
                tryToUpdateKnowledgeBaseExceptionStatus.rejected,
                (state, action) => {
                    state.error =
                        (action.payload as string) || 'Failed to update status';
                },
            )
            .addCase(
                tryToUpdateKnowledgeBaseExceptionStatus.fulfilled,
                (state, action: PayloadAction<KBException>) => {
                    const updated = action.payload;
                    // update list
                    const idx = state.list.findIndex(
                        (x) => x._id === updated._id,
                    );
                    if (idx >= 0) {
                        state.list[idx] = updated;
                    }
                    // update detail
                    if (state.detail && state.detail._id === updated._id) {
                        state.detail = updated;
                    }
                },
            )

            // Resolve
            .addCase(
                tryToResolveKnowledgeBaseException.rejected,
                (state, action) => {
                    state.error =
                        (action.payload as string) ||
                        'Failed to resolve exception';
                },
            )
            .addCase(
                tryToResolveKnowledgeBaseException.fulfilled,
                (state, action: PayloadAction<KBException>) => {
                    const updated = action.payload;
                    // update list
                    const idx = state.list.findIndex(
                        (x) => x._id === updated._id,
                    );
                    if (idx >= 0) {
                        state.list[idx] = updated;
                    }
                    // update detail
                    if (state.detail && state.detail._id === updated._id) {
                        state.detail = updated;
                    }
                },
            )
            .addCase(
                tryToResolveKnowledgeBaseExceptionConfirm.rejected,
                (state, action) => {
                    state.error =
                        (action.payload as string) ||
                        'Failed to confirm-resolve exception';
                },
            )
            .addCase(
                tryToResolveKnowledgeBaseExceptionConfirm.fulfilled,
                (state, action: PayloadAction<KBException>) => {
                    const updated = action.payload;
                    const idx = state.list.findIndex(
                        (x) => x._id === updated._id,
                    );
                    if (idx >= 0) state.list[idx] = updated;
                    if (state.detail && state.detail._id === updated._id)
                        state.detail = updated;
                },
            )
            // Confirm resolve
            .addCase(
                tryToConfirmResolveKnowledgeBaseException.rejected,
                (state, action) => {
                    state.error =
                        (action.payload as string) ||
                        'Failed to confirm resolve exception';
                },
            )
            .addCase(
                tryToConfirmResolveKnowledgeBaseException.fulfilled,
                (state, action: PayloadAction<KBException>) => {
                    const updated = action.payload;

                    const idx = state.list.findIndex(
                        (x) => x._id === updated._id,
                    );
                    if (idx >= 0) state.list[idx] = updated;

                    if (state.detail && state.detail._id === updated._id) {
                        state.detail = updated;
                    }
                },
            );
    },
});

export const { clearKnowledgeBaseExceptionsError } =
    knowledgeBaseExceptionsSlice.actions;

export default knowledgeBaseExceptionsSlice.reducer;
